export const REDACTED_LABEL = "[redacted]";

export interface RedactableUser {
  id?: string;
  name?: string | null;
  first_name?: string | null;
  profile_picture_url?: string | null;
  redacted_fields?: string[];
}

export const buildRedactedPseudoId = (userId: string | undefined): string => {
  if (!userId) return REDACTED_LABEL;
  return `#${userId.split("-")[0]?.toUpperCase() ?? "XXXX"}`;
};

export const isFieldRedacted = (
  field: string,
  redactedFields: string[] | undefined,
  _rawValue?: unknown,
): boolean => {
  return redactedFields?.includes(field) ?? false;
};

export const inferRedactedFieldsFromNulls = (
  fields: Record<string, unknown>,
): string[] =>
  Object.entries(fields)
    .filter(([, v]) => v === null)
    .map(([k]) => k);

export const getMenteeDisplayName = (
  mentee: RedactableUser | null | undefined,
): string => {
  if (!mentee) return REDACTED_LABEL;
  if (isFieldRedacted("name", mentee.redacted_fields, mentee.name)) {
    return buildRedactedPseudoId(mentee.id);
  }
  return mentee.name ?? "";
};

export const getMenteeDisplayFirstName = (
  mentee: RedactableUser | null | undefined,
): string => {
  if (!mentee) return REDACTED_LABEL;
  const firstNameRedacted = isFieldRedacted(
    "first_name",
    mentee.redacted_fields,
    mentee.first_name,
  );
  const fullNameRedacted = isFieldRedacted(
    "name",
    mentee.redacted_fields,
    mentee.name,
  );
  // Either name shape can be redacted independently, so avoid partial names.
  if (firstNameRedacted || fullNameRedacted) {
    return buildRedactedPseudoId(mentee.id);
  }
  if (mentee.first_name) return mentee.first_name;
  if (mentee.name) return mentee.name.split(" ")[0] ?? "";
  return "";
};

export const getMenteeDisplayPhoto = (
  mentee: RedactableUser | null | undefined,
): string | undefined => {
  if (!mentee) return undefined;
  if (
    isFieldRedacted(
      "profile_picture_url",
      mentee.redacted_fields,
      mentee.profile_picture_url,
    )
  ) {
    return undefined;
  }
  return mentee.profile_picture_url ?? undefined;
};

export const renderRedactableField = (
  field: string,
  rawValue: string | null | undefined,
  redactedFields: string[] | undefined,
  fallback: string = "-",
): string => {
  if (isFieldRedacted(field, redactedFields, rawValue)) return REDACTED_LABEL;
  return rawValue || fallback;
};

interface SendbirdMessageUserLike {
  user_id?: string;
  nickname?: string;
  metadata?: { role?: string };
}

export const getSendbirdMessageSenderDisplayName = (
  user: SendbirdMessageUserLike | undefined,
): string | null => {
  if (!user) return null;
  if (user.nickname) return user.nickname;
  if (user.metadata?.role === "MENTEE") {
    return buildRedactedPseudoId(user.user_id);
  }
  return null;
};
